import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PODYear, OutcomeService, Outcome, OutcomeRequestApi, OutcomeGrantApi, OutcomePwapDiversions, OutcomeEventApi } from '../../services/outcome.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { PrintService } from '../../services/print.service';
import { ActivatedRoute, Router } from '@angular/router';
import * as XLSX from 'xlsx'; // SPREADSHEET RELATED
import { WorkBook, WorkSheet } from 'xlsx';

enum SortDirection {
  asc = 'asc',
  desc = 'desc'
}

@Component({
  selector: 'app-evaluate-outcomes',
  templateUrl: './evaluate-outcomes.component.html',
  styleUrls: ['./evaluate-outcomes.component.scss']
})
export class EvaluateOutcomesComponent implements OnInit {

  showDate = true;
  showData = false;
  year: PODYear = null;
  years: number[] = [];
  yearDates: Date[] = [];
  direction = SortDirection.asc;
  // Allow use in template
  SortDirection = SortDirection;
  column: string;

  pwapValues: OutcomePwapDiversions[] = [];
  requests: OutcomeRequestApi[] = [];
  grants: OutcomeGrantApi[] = [];
  allocationsData: OutcomeEventApi[] = [];
  outcomeData: {[id: string]: Outcome} = {};
  outcomeGrantData: {[id: string]: OutcomeGrantApi[]} = {};
  requestsOfLocation: OutcomeRequestApi[] = [];

  showDateSingle = true;
  showDataSingle = true;
  selectedOutcomeData: Outcome = null;

  private unsubscribe: Subject<any> = new Subject();

  constructor(
    private outcomeService: OutcomeService,
    private printService: PrintService,
    private modalService: NgbModal,
    private router: Router,
    private route: ActivatedRoute,
  ) { }

  ngOnInit() {
    this.year = { year: new Date().getFullYear() };
    this.years = [];
    this.outcomeService.getYears();

    this.outcomeService.changePODYear(this.year);
    this.outcomeService.currentYears$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(years => {
        this.years = years;
        if (years == null) return;
        this.updateYear({ target: { value: years[years.length - 1] } });
    });

    this.outcomeService.currentPODYear$
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(podyear => {
      this.year = podyear;
      if (!!this.year) {
        this.getOutcomeData(this.year.year);
      }
    });

    this.outcomeService.currentGrants$
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(data => {
      this.grants = data;
    });

    this.outcomeService.currentPwapValues$
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(data => {
      this.pwapValues = data;
      if (this.requests && this.requests.length >= 0 &&
          this.grants && this.grants.length >= 0 &&
          this.pwapValues && this.pwapValues.length >=0 &&
          this.allocationsData && this.allocationsData.length >= 0) {
        this.setupOutcomeData(this.year.year);
      }
    });

    this.outcomeService.currentRequests$
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(data => {
      this.requests = data;
      if (this.requests && this.requests.length >= 0 &&
          this.grants && this.grants.length >= 0 &&
          this.pwapValues && this.pwapValues.length >=0 &&
          this.allocationsData && this.allocationsData.length >= 0) {
        this.setupOutcomeData(this.year.year);
      }
    });

    this.outcomeService.currentEventsData$
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(data => {
      this.allocationsData = data;
      if (this.requests && this.requests.length >= 0 &&
          this.grants && this.grants.length >= 0 &&
          this.pwapValues && this.pwapValues.length >= 0 &&
          this.allocationsData && this.allocationsData.length >= 0) {
        this.setupOutcomeData(this.year.year);
      }
    })
  }

  getOutcomeData(year: number) {
    this.outcomeService.getGrants(year);
    this.outcomeService.getRequests(year);
    this.outcomeService.getReports(year);
    this.outcomeService.getPwapValues(year);
    this.outcomeService.getEventValues(year);
  }

  setupOutcomeData(year: number) {
    this.outcomeData = {};
    if (!this.requests) {
      return;
    }

    // create grant dictionary
    this.grants.forEach(grant => {
      const key = `${grant.appropriatorName},${grant.permitPointOfDiversionName}`;
      if (key in this.outcomeGrantData) {
        const foundDuplicate = this.outcomeGrantData[key]
          .some(apiData => apiData.applicationNumber === grant.applicationNumber);
        if (!foundDuplicate) {
          this.outcomeGrantData[key].push(grant);
        }
      } else {
        this.outcomeGrantData[key] = [];
        this.outcomeGrantData[key].push(grant);
      }
    });

    // create outcome data templates
    this.requests.forEach(req => {
      const key = `${req.appropriatorName},${req.permitPointOfDiversionName}`;
      if (key in this.outcomeData) {
        // apply this request to outcomeData
        this.applyRequestData(this.outcomeData[key], req);
      } else {
        this.outcomeData[key] = this.createOutcomeData(req);
      }
    });

    this.allocationsData.forEach(allo => {
      const key = `${allo.appropriatorName},${allo.permitPointOfDiversionName}`;
      if (key in this.outcomeData) {
        this.applyEventData(this.outcomeData[key], allo);
      } else {
        this.outcomeData[key] = this.createOutcomeData(allo);
      }
    });

    if (!!this.outcomeData && Object.keys(this.outcomeData).length > 0) {
      for (var okey in this.outcomeData) {
        const ocdata = this.outcomeData[okey];
        this.yearDates = ocdata.dates;
        break;
      }
    } else {
      this.yearDates = [];
    }
  }

  applyRequestData(outcome: Outcome, req: OutcomeRequestApi) {
    const firstDate = new Date(this.year.year, 0, 1);
    const reqStartDate = new Date(req.startDate);
    const reqEndDate = new Date(req.endDate);

    if (reqStartDate < firstDate) return;

    outcome.dates.forEach((date, index) => {
      let update = false;
      if (date >= reqStartDate) {
        if (reqEndDate > firstDate && reqEndDate > reqStartDate) {
          if (date <= reqEndDate) {
            update = true;
          }
        } else {
          update = true;
        }
      }
      if (update) {
        outcome.requests[index] = req.requestCFS;
      }
    });
  }

  applyEventData(outcome: Outcome, event: OutcomeEventApi) {
    const firstDate = new Date(this.year.year, 0, 1);
    const allocationStartDate = new Date(event.startDate);
    const allocationEndDate = new Date(event.endDate);

    if (allocationStartDate < firstDate) return;

    outcome.dates.forEach((date, index) => {
      let update = false;
      if (date >= allocationStartDate) {
        if (allocationEndDate > firstDate && allocationEndDate > allocationStartDate) {
          if (date <= allocationEndDate) {
            update = true;
          }
        } else {
          update = true;
        }
      }
      if (update) {
        outcome.requests[index] = event.allocated;
      }
    });
  }

  // Not used??
  applyGrantData() {
    const firstDate: Date = new Date(this.year.year, 0, 1);
    this.grants.forEach(grant => {
      let outcome: Outcome = null;
      const keyGrant = `${grant.appropriatorName},${grant.permitPointOfDiversionName}`;
      if (keyGrant in this.outcomeData) {
        outcome = this.outcomeData[keyGrant];
      }

      if (!outcome) return;

      const grantStartDate = new Date(grant.permitStartDate);
      const grantEndDate = new Date(grant.permitEndDate);
      if (grantStartDate < firstDate) return;

      outcome.dates.forEach((date, index) => {
        let update = false;
        if (date >= grantStartDate) {
          if (grantEndDate > firstDate && grantEndDate > grantStartDate) {
            if (date <= grantEndDate) {
              update = true;
            }
          } else {
            update = true;
          }
        }
        if (update) {
          outcome.grants[index] = grant.grant;
        }
      })
    })
  }

  createOutcomeData(req: OutcomeRequestApi | OutcomeEventApi): Outcome {
    let data = {
      dates: [],
      requests: [],
      actuals: [],
      grants: [],
      waterRightsIds: [],
      applicationNumbers: [],
      permitName: '',
      project: '',
      appropriatorName: req.appropriatorName,
      permitPointOfDiversionName: req.permitPointOfDiversionName,
      appropriatorId: req.appropriatorId,
      permitAnnualOperatingPlanId: req.permitAnnualOperatingPlanId,
      permitPointOfDiversionId: req.permitPointOfDiversionId,
    };

    const key = `${req.appropriatorName},${req.permitPointOfDiversionName}`;
    const year = this.year.year;
    const startDate: Date = new Date(`${year}/1/1`);
    const daysInYearZeroIndexed = this.leapYear(year) ? 365 : 364;

    for (let i = 0; i <= daysInYearZeroIndexed; i++) {
      // add date
      const newDate = new Date(`${year}/1/1`);
      newDate.setDate(newDate.getDate() + i);
      data.dates.push(newDate);

      const reqStartDate = new Date(req.startDate);
      const reqEndDate = new Date(req.endDate);
      let update = false;
      if (newDate >= reqStartDate) { // 1st must be > request's start date
        if(reqEndDate > startDate && reqEndDate > reqStartDate) { // if there is a legit request end date
          if (newDate <= reqEndDate) { // then check if < request's end date
            update = true;
          }
        }
        else {
          update = true;
        }
      }
      if (update) {
        if ('allocated' in req) {
          data.requests.push(req.allocated);
        } else {
          data.requests.push(req.requestCFS);
        }
      } else {
        data.requests.push(0);
      }

      if (key in this.outcomeGrantData) {
        const outcomeGrants = this.outcomeGrantData[key];
        outcomeGrants.forEach(ocGrant => {
          const gStartDate = new Date(ocGrant.permitStartDate);
          const gEndDate = new Date(ocGrant.permitEndDate);
          update = false;
          if (newDate >= gStartDate) { // 1st must be > grantuest's start date
            if(gEndDate > startDate && gEndDate > gStartDate) { // if there is a legit grantuest end date
              if (newDate <= gEndDate) { // then check if < grantuest's end date
                update = true;
              }
            }
            else {
              update = true;
            }
          }
          if (update) {
            data.grants.push(ocGrant.grant);
            data.applicationNumbers.push(ocGrant.applicationNumber);
            data.waterRightsIds.push(ocGrant.waterRightsId);
          }
          else {
            data.grants.push(0);
            data.applicationNumbers.push('');
            data.waterRightsIds.push('');
          }
        });
      }

      data.actuals.push(0);
      // add diversions
      let pwapdate = new Date(Date.now());
      this.pwapValues.forEach(pwaptser => {
        let pwapValueIndex = 0;
        if (pwaptser.permitPointOfDiversionName === req.permitPointOfDiversionName) {
          for (var t = pwapValueIndex; t < pwaptser.pwapValues.length; t++) {
            pwapdate = new Date(pwaptser.pwapValues[t].pwapReportDate);
            if (pwapdate < newDate) {
              continue;
            }
            else if(pwapdate > newDate) {
              pwapValueIndex = t;
              break;
            }
            else {
              pwapValueIndex = t;
              data.actuals[data.actuals.length -1] = pwaptser.pwapValues[t].value;
              break;
            }
          }
        }
      });
    }

    return data;
  }

  leapYear(year: number) {
    return (year % 4 == 0 && year % 100 != 0) || year % 400 == 0;
  }

  updateYear($event) {
    this.year = null;
    this.year = {
      year: +$event.target.value,
    };
    // clean up the selected year's data, so as to get a fresh new year's data
    this.pwapValues = [];
    this.requests = [];
    this.grants = [];
    this.outcomeData = {};
    this.outcomeGrantData = {};
    this.requestsOfLocation = [];
    this.outcomeService.changePODYear(this.year);
  }

   // Sorting function
  onSort(_$event) {
    this.direction = this.direction === SortDirection.asc ? SortDirection.desc : SortDirection.asc;
  }

  setTargetData(outcomeValue) {
    this.selectedOutcomeData = outcomeValue;
    const key = `${outcomeValue.appropriatorName},${outcomeValue.permitPointOfDiversionName}`;
    this.requestsOfLocation = [];
    const yearStart = new Date(this.year.year, 0, 1);
    this.requests.forEach(req => {
      if (`${req.appropriatorName},${req.permitPointOfDiversionName}` === key) {
        const startDate = new Date(req.startDate);
        if (startDate >= yearStart) {
          this.requestsOfLocation.push(req);
        }
      }
    });
  }

  open(content) {
    this.modalService.open(content, {ariaLabelledBy: 'modal-notes'});
  }

  print() {
    this.router.navigate(['/report'], {queryParams: { reportyear: `${this.year.year}`}});
  }

  export(): void {
    /* generate worksheet */
    var fileName = `Export-Outcomes-${this.year.year}.xlsx`;
    var tableHtml = document.getElementById('outcometableexport');
    const ws: WorkSheet = XLSX.utils.table_to_sheet(tableHtml);

    /* generate workbook and add the worksheet */
    const wb: WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    /* save to file */
    XLSX.writeFile(wb, fileName);
  }
}
